import { useState } from 'react';
import { Check, ChefHat, CreditCard, Play } from 'lucide-react';
import { motion } from 'motion/react';
import useOrderTimer from './useOrderTimer';
import { nextActions, prepStationOf, STATE_LABEL, STATE_TONE, tagFor, urgencyTier } from './serviceColors';
import GuideModal, { itemGuide } from '../GuideModal';

function itemCategory(item) {
  return item.productId?.category || item.category || '';
}

function actionIcon(status) {
  if (status === 'preparing') return <ChefHat size={17} />;
  if (status === 'served') return <Check size={17} />;
  if (status === 'paid') return <CreditCard size={17} />;
  return <Play size={17} />;
}

export default function OrderCard({ order, table, onStatusChange, stationFilter = 'all' }) {
  const [guide, setGuide] = useState(null);
  const minutes = useOrderTimer(order);
  const urgency = urgencyTier(minutes);
  const scopedItems = (order.items || []).filter((item) => stationFilter === 'all' || prepStationOf(item) === stationFilter);
  const liveStatuses = scopedItems
    .map((item) => item.status || order.status)
    .filter((status) => !['served', 'paid', 'cancelled'].includes(status));
  const effectiveStatus = liveStatuses[0] || scopedItems[0]?.status || order.status;
  const tone = STATE_TONE[effectiveStatus] || 'faint';
  const actions = nextActions(effectiveStatus);

  return (
    <motion.article
      className={`service-order-card service-order-${tone} service-urgency-${urgency}`}
      layout
      initial={{ opacity: 0, scale: 0.96, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96, y: -8 }}
      transition={{ duration: 0.22 }}
    >
      <header className="service-order-card-head">
        <div>
          <strong>M{table?.number || order.tableId?.number || '?'}</strong>
          <span>{new Date(order.createdAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
          <span className={`service-timer service-timer-${urgency}`}>{minutes} min</span>
        </div>
        <em>{stationFilter === 'all' ? (STATE_LABEL[order.status] || order.status) : `${stationFilter === 'barra' ? 'Barra' : 'Cocina'} · ${STATE_LABEL[effectiveStatus] || effectiveStatus}`}</em>
      </header>

      <div className="service-order-items">
        {order.items?.map((item, index) => {
          const category = itemCategory(item);
          const tag = tagFor(category);
          const station = prepStationOf(item);
          const dimmed = stationFilter !== 'all' && station !== stationFilter;
          const itemGuideData = itemGuide(item);
          return (
            <div key={`${order._id}-${item.productId?._id || item.productId || index}`} className={dimmed ? 'service-order-item service-order-item-dimmed' : 'service-order-item'}>
              <span className={`service-kitchen-tag service-kitchen-tag-${tag.toLowerCase()}`}>{tag}</span>
              <strong>{item.quantity}x</strong>
              <span>
                {item.nameSnapshot}
                {itemGuideData && (
                  <button type="button" className="ko-btn-guide" onClick={() => setGuide(itemGuideData)} title={`Guía de ${itemGuideData.name}`}>
                    Guía
                  </button>
                )}
              </span>
              <em>{(Number(item.priceSnapshot || 0) * Number(item.quantity || 1)).toFixed(2)} €</em>
              <small className="service-item-status">{STATE_LABEL[item.status || order.status] || item.status || order.status}</small>
              {item.notes && <small>{item.notes}</small>}
            </div>
          );
        })}
      </div>

      <footer className="service-order-card-foot">
        <div>
          <span>Total</span>
          <strong>{Number(order.total || 0).toFixed(2)} €</strong>
        </div>
        <div className="service-order-actions">
          {actions.map((action) => (
            <button key={action.status} type="button" onClick={() => onStatusChange(order, action.status, { station: stationFilter })}>
              {actionIcon(action.status)}
              {action.label}
            </button>
          ))}
        </div>
      </footer>

      {guide && <GuideModal guide={guide} onClose={() => setGuide(null)} />}
    </motion.article>
  );
}
